import { createElement } from "react";
import rhtToast from "react-hot-toast";
import { toUserMessage } from "./userMessage";
import { getFloatingNavInset } from "@/common/components/layouts/bottomNavigation/floatingBottomNavigation";

/**
 * The messenger's short messages: one quiet dark pill above the composer / nav, like Telegram's.
 * No icons, no coloured card, no heavy shadow; an optional text action («برگرداندن») on the end.
 * Error / success / warning look the same on purpose, the words carry the meaning.
 */
function Snackbar({ t, message, action, inset = 0 }) {
  return createElement(
    "div",
    {
      dir: "rtl",
      role: "status",
      "aria-live": "polite",
      className:
        "pointer-events-auto flex max-w-[min(92vw,420px)] items-center gap-3 rounded-[22px] bg-[rgba(30,30,32,0.86)] px-4 py-2.5 text-[14px] font-medium leading-[1.5] text-white backdrop-blur-xl [-webkit-backdrop-filter:blur(20px)]",
      style: { opacity: t.visible ? 1 : 0, transition: "opacity 180ms ease", marginBottom: inset },
    },
    createElement("span", { className: "min-w-0 break-words" }, message),
    action
      ? createElement(
          "button",
          {
            type: "button",
            className:
              "relative ms-1 shrink-0 cursor-pointer border-0 bg-transparent p-0 text-[14px] font-semibold text-[#6ab3f3] [font-family:inherit] before:absolute before:-inset-x-2 before:-inset-y-3",
            onClick: () => {
              rhtToast.dismiss(t.id);
              action.onClick?.();
            },
          },
          action.label
        )
      : null
  );
}

/** @param {string} message @param {{ action?: { label: string, onClick: () => void }, duration?: number }} [opts] */
/** Inside a chat the pill sits just above the composer, not on top of it. */
function snackbarBottomInset() {
  if (typeof document === "undefined") return getFloatingNavInset();
  const form = document.querySelector("textarea.reply-form__input")?.closest("form");
  if (form) {
    const top = form.getBoundingClientRect().top;
    if (top > 0) return Math.max(0, Math.round(window.innerHeight - top)) + 8;
  }
  return getFloatingNavInset();
}

export function showSnackbar(message, { action, duration } = {}) {
  if (!message) return;
  if (duration == null) duration = action ? 3500 : 2000;
  const inset = snackbarBottomInset();
  rhtToast.custom((t) => createElement(Snackbar, { t, message, action, inset }), {
    position: "bottom-center",
    duration,
  });
}

/** @param {string} message */
export function showErrorToast(message) {
  if (!message) return;
  showSnackbar(typeof message === "string" ? toUserMessage(message) : message, { duration: 2500 });
}

/** @param {string} message */
export function showSuccessToast(message) {
  if (!message) return;
  showSnackbar(typeof message === "string" ? toUserMessage(message, "انجام شد.") : message, { duration: 2000 });
}

/** @param {string} message */
export function showWarningToast(message) {
  if (!message) return;
  showSnackbar(typeof message === "string" ? toUserMessage(message) : message, { duration: 2500 });
}

/**
 * Drop-in for react-hot-toast's `toast`, `toast.success`, `toast.error`: every message of the
 * messenger goes through the same quiet pill, even where it was called directly.
 */
export const toast = Object.assign((message, opts) => showSnackbar(message, opts), {
  success: (message, opts) => showSnackbar(message, opts),
  error: (message, opts) => showSnackbar(message, { duration: 2500, ...opts }),
  dismiss: (id) => rhtToast.dismiss(id),
});
